/**
 * 3.0 스레드 DTO → 화면 모델.
 * @description 고정 항목을 먼저, 그다음 최신순 — 서버가 정한 순서 그대로 잇는다(앱과 같다). 웹은
 *   맛보기만 보여주므로 앞에서 `max` 개만 남긴다. 각 항목은 앱에서 그 글을 여는 경로를 갖는다.
 */
import { toCollabV3Person } from "./collabV3PersonMapper";
import { collabV3AppPaths } from "../utils/collabV3AppPaths";
import { safeHttpUrl } from "../utils/safeHttpUrl";
import type { CollabV3ThreadDto, CollabV3ThreadItemDto } from "../dtos";
import type {
  CollabV3Thread,
  CollabV3ThreadItem,
  CollabV3ThreadLink,
} from "../entities";

/** 글의 첨부. 첨부가 없어도 바깥 주소(옛 글 · 작품의 링크)가 있으면 링크로 본다 */
function toLink(item: CollabV3ThreadItemDto): CollabV3ThreadLink | null {
  const attachment = item.attachment;
  if (!attachment) {
    const href = safeHttpUrl(item.linkUrl);
    return href
      ? { type: "URL", isAvailable: true, title: null, imageUrl: null, href }
      : null;
  }
  const isUrl = attachment.type === "URL";
  return {
    type: attachment.type,
    isAvailable: attachment.isAvailable,
    title: attachment.title?.trim() || null,
    // 바깥 주소의 미리보기 그림은 서버가 주지 않는다(앱이 OG 를 읽는다)
    imageUrl: isUrl ? null : attachment.imageUrl?.trim() || null,
    href: isUrl ? safeHttpUrl(attachment.url ?? item.linkUrl) : null,
  };
}

function toItem(
  item: CollabV3ThreadItemDto,
  collabId: number,
  isPinned: boolean,
): CollabV3ThreadItem {
  const execution = item.fundingExecution;
  const paths = collabV3AppPaths(collabId);
  return {
    key: `${item.itemType}-${item.itemId}`,
    type: item.itemType,
    isPinned,
    author: {
      ...toCollabV3Person(item.author),
      isOrganizer: item.author.isOrganizer,
    },
    content: item.content?.trim() ?? "",
    imageUrls: item.imageUrls.filter(Boolean),
    isSelected: item.work?.isSelected ?? false,
    link: toLink(item),
    execution: execution
      ? {
          usages: execution.usages,
          usedAmount: execution.usedAmount,
          leftoverNote: execution.leftoverNote?.trim() || null,
        }
      : null,
    pickedWorks: (item.announcement?.selectedWorks ?? []).map((work) => ({
      workId: work.workId,
      thumbnailUrl: work.thumbnailUrl?.trim() || null,
      authorName: work.author.displayName,
      message: work.message?.trim() || null,
    })),
    likeCount: item.likeCount,
    commentCount: item.commentCount,
    createdAt: item.createdAt,
    appPath:
      item.itemType === "WORK"
        ? paths.work(item.itemId)
        : paths.threadItem(item.itemId),
  };
}

export const toCollabV3Thread = (
  dto: CollabV3ThreadDto,
  collabId: number,
  max: number,
): CollabV3Thread => ({
  items: [
    ...dto.pinned.map((item) => toItem(item, collabId, true)),
    ...dto.data.map((item) => toItem(item, collabId, false)),
  ].slice(0, max),
});
